import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ShieldCheck, Plus, Trash2, Route } from 'lucide-react'
import { SearchInput } from '../components/SearchInput'
import { useAuth } from '../store/auth'
import { getRoles, createRole } from '../api/auth'
import { Modal } from '../components/Modal'
import { EmptyState } from '../components/EmptyState'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { useDateFmt } from '../hooks/useDateFmt'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import type { Role } from '../types'

export function Roles() {
  const fmt = useDateFmt()
  const { user: me } = useAuth()
  const businessId = me!.businessId
  const qc = useQueryClient()
  const navigate = useNavigate()

  const { data: roles = [], isLoading } = useQuery({
    queryKey: ['roles', businessId],
    queryFn: () => getRoles(businessId).then((r) => r.data),
  })

  // Create role
  const [showCreate, setShowCreate] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDesc, setNewDesc] = useState('')

  const create = useMutation({
    mutationFn: () => createRole({ businessId, name: newName, description: newDesc }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['roles'] }); setShowCreate(false); setNewName(''); setNewDesc('') },
  })

  const [search, setSearch] = useState('')
  const filtered = roles.filter((r: Role) =>
    [r.name, r.description ?? ''].some((v) => v.toLowerCase().includes(search.toLowerCase()))
  )

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null)

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Roles</h1>
          <p className="text-sm text-ink-dim mt-0.5">Manage access roles for your organisation</p>
        </div>
        <div className="flex items-center gap-2">
          <SearchInput value={search} onChange={setSearch} placeholder="Search roles" />
          <Button icon={Plus} onClick={() => setShowCreate(true)}>New role</Button>
        </div>
      </div>

      <div className="table-wrap">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-ink-dim">Loading</div>
        ) : roles.length === 0 ? (
          <EmptyState icon={ShieldCheck} title="No roles yet" action={
            <button onClick={() => setShowCreate(true)} className="text-sm text-ink font-medium hover:underline">Create first role</button>
          } />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((r: Role) => (
                <tr key={r.id}>
                  <td className="font-medium text-ink">{r.name}</td>
                  <td className="text-ink-dim">{r.description ?? ''}</td>
                  <td className="text-ink-dim">{fmt(r.createdAt)}</td>
                  <td>
                    <div className="flex items-center gap-3 justify-end">
                      <button
                        onClick={() => navigate(`/roles/${r.id}/paths`)}
                        className="flex items-center gap-1 text-xs text-ink-dim hover:text-ink font-medium"
                      >
                        <Route size={13} /> Paths
                      </button>
                      <button
                        onClick={() => setDeleteTarget(r)}
                        className="text-ink-dim hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create modal */}
      {showCreate && (
        <Modal title="New role" onClose={() => setShowCreate(false)} size="sm">
          <div className="space-y-4">
            <Field label="Name *">
              <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. support-agent"
                className="field" />
            </Field>
            <Field label="Description">
              <input value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="Optional description"
                className="field" />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={() => create.mutate()} disabled={!newName} loading={create.isPending}>
                Create
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <ConfirmDialog
          title="Delete role"
          message={`Delete role "${deleteTarget.name}"? This cannot be undone.`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => setDeleteTarget(null)}
        />
      )}
    </div>
  )
}
